import { CART_TRANSFORM_FLAG, isCartTransformHandle } from "@root/constants/cart-transform";

// Cart line attributes for custom covers priced by the Shopify Cart Transform.
// The line keeps the exact keys the PMS draft order puts on the order today
// (public measurements and customizations, `_measurements`, `_squareFeet`, …),
// plus the PMS-signed price and the inputs needed to recalculate it.
// The `_item_*` analytics keys are appended later in components/product.

const CUSTOMIZATION_PREFIX = "c_";
const REFERENCE_IMAGE_PREFIX = "referenceImage";

const toAttributes = (entries) => entries
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(([key, value]) => ({ key, value: value.toString() }));

/**
 * @param {object} args
 * @param {object} args.product          PMS product (handle, measurements [{ key, name }])
 * @param {object} args.customAttributes patio-calculator attributes (C01…, c_…, referenceImage…, specialRequest)
 * @param {object} args.processedFiles   uploaded file URLs (_productImage, referenceImage…, c_Logo)
 * @param {object} args.priceData        price_check response (finalPrice, priceSignature, formulaParams, customizationIds)
 * @returns {{ key: string, value: string }[] | null} null when the product does not use the Cart Transform flow
 */
const buildCartTransformAttributes = ({ product, customAttributes, processedFiles, priceData }) => {
    if (!isCartTransformHandle(product?.handle)) return null;

    const values = { ...customAttributes, ...processedFiles };
    const customizations = Object.keys(values)
        .filter((key) => key.startsWith(CUSTOMIZATION_PREFIX) && values[key])
        .map((key) => key.slice(CUSTOMIZATION_PREFIX.length));
    const referenceImages = Object.keys(values)
        .filter((key) => key.startsWith(REFERENCE_IMAGE_PREFIX))
        .sort((first, second) => first.localeCompare(second, undefined, { numeric: true }));

    return toAttributes([
        // Public: shown to the customer and read by production.
        ...(product?.measurements || []).map(({ key, name }) => [name, values[key]]),
        ...customizations.map((name) => [name, values[`${CUSTOMIZATION_PREFIX}${name}`]]),
        ...referenceImages.map((key, index) => [index === 0 ? "Image" : `Image ${index + 1}`, values[key]]),
        ["Special Request", values.specialRequest],

        // Private: pricing (verified by the Cart Transform function).
        ["_customProduct", "Yes"],
        [CART_TRANSFORM_FLAG, "Yes"],
        ["_final_price", priceData?.finalPrice],
        ["_price_signature", priceData?.priceSignature],
        ["_formula_params", priceData?.formulaParams],
        ["_customization_ids", priceData?.customizationIds],

        // Private: production.
        ["_measurements", (product?.measurements || []).map(({ name }) => name).join("|")],
        ["_customizations", customizations.join("|")],
        ["_squareFeet", values._squareFeet],
        ["_cutPieces", values._cutPieces],
        ["_productImage", values._productImage],
    ]);
};

/**
 * Converts a Cart Transform line back to the attribute shape the configurator
 * edits (C01…, c_…, customization_variant_ids, referenceImage…, specialRequest).
 * @param {{ key: string, value: string }[]} attributes
 * @returns {{ key: string, value: string }[]}
 */
export const toConfiguratorAttributes = (attributes = []) => {
    const byKey = Object.fromEntries(attributes.map(({ key, value }) => [key, value]));
    const imageKeys = Object.keys(byKey).filter((key) => /^Image( \d+)?$/.test(key));
    let formulaParams = {};

    try {
        formulaParams = JSON.parse(byKey._formula_params || "{}");
    } catch {
        formulaParams = {};
    }

    return toAttributes([
        ["_customProduct", "Yes"],
        ...Object.entries(formulaParams).filter(([key]) => /^C\d+$/.test(key)),
        ...(byKey._customizations || "").split("|").filter(Boolean)
            .map((name) => [`${CUSTOMIZATION_PREFIX}${name}`, byKey[name]]),
        ["customization_variant_ids", byKey._customization_ids],
        ...imageKeys.map((key, index) => [`${REFERENCE_IMAGE_PREFIX}${index === 0 ? "" : index + 1}`, byKey[key]]),
        ["specialRequest", byKey["Special Request"]],
        ["_productImage", byKey._productImage],
    ]);
};

export default buildCartTransformAttributes;
